import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Badge, Empty, NavBar, PullToRefresh, Skeleton, Tabs, Tag} from 'antd-mobile';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {getNavBarContainerStyle} from '../../../../utils/deviceUtils';
import useDspWarehouseStore from '../../store/dspWarehouseStore';
import useEnsureDspContext from '../../hooks/useEnsureDspContext';
import {
    GET_CUSTOMER_COMPLAINT_LIST_BY_FILTERS,
    GetCustomerComplaintListByFiltersResponse,
    GetCustomerComplaintListByFiltersVariables,
} from './graphql/queries/getCustomerComplaintListByFilters';
import {
    ComplaintStatus,
    CustomerComplaint,
    DSP_VISIBLE_COMPLAINT_TYPES,
} from './graphql/types/CustomerComplaintType';
import {DspPath} from '../../../../enums/DeliveryNavigatePathEnum';
import {
    GET_DSP_COMPLAINT_HISTORY_UNREAD_COUNT,
    GetDspComplaintHistoryUnreadCountResponse,
    GetDspComplaintHistoryUnreadCountVariables,
} from './graphql/queries/getDspComplaintHistoryUnreadCount';
import {
    DspComplaintHistoryTab,
    MARK_DSP_COMPLAINT_HISTORY_TAB_AS_READ,
    MarkDspComplaintHistoryTabAsReadResponse,
    MarkDspComplaintHistoryTabAsReadVariables,
} from './graphql/mutations/markDspComplaintHistoryAsRead';
import ComplaintFilterBar from './components/ComplaintFilterBar';
import ComplaintCard from './components/ComplaintCard';
import {useComplaintStatusText} from './hooks/useComplaintStatusText';
import {formatTime, matchKeyword} from './utils/complaintListUtils';
import {BarCodeScanner} from '../../../../components/BarCodeScanner';
import {useNetwork} from '../../../../hooks/useNetwork';

// 后端按 isFinished=1 一次性拉回全部已完结记录，前端按 status 分到三个 tab，可同时展示每个 tab 的数量
const TAB_TO_STATUS_LIST: Record<DspComplaintHistoryTab, string[]> = {
    APPEAL_APPROVED: [ComplaintStatus.REVOKED],
    APPEAL_REJECTED: [ComplaintStatus.ENFORCED],
    ACKNOWLEDGED: [ComplaintStatus.ACKNOWLEDGED, ComplaintStatus.TIMEOUT, ComplaintStatus.CLOSED],
};

const ACKED_TAB_STATUS_TAG_COLOR: Record<string, string> = {
    [ComplaintStatus.ACKNOWLEDGED]: 'warning',
    [ComplaintStatus.TIMEOUT]: 'danger',
    [ComplaintStatus.CLOSED]: 'default',
};

const DEFAULT_LOOKBACK_DAYS = 14;

const CustomerComplaintHistoryPage: React.FC = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {getStatusText} = useComplaintStatusText();
    const {isOnline} = useNetwork();
    // 刷新 / URL 直达本页时未经过首页，确保 store 的 dspId / warehouses 就绪，否则下方查询会被 skip
    useEnsureDspContext();

    const dspId = useDspWarehouseStore((state) => state.dspId);
    const selectedWarehouseId = useDspWarehouseStore((state) => state.selectedWarehouseId);
    const warehouseId = useMemo(() => {
        if (!selectedWarehouseId) return undefined;
        const n = Number(selectedWarehouseId);
        return Number.isFinite(n) ? n : undefined;
    }, [selectedWarehouseId]);

    const [activeTab, setActiveTab] = useState<DspComplaintHistoryTab>('APPEAL_APPROVED');
    const [keyword, setKeyword] = useState('');
    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [locallyReadTabs, setLocallyReadTabs] = useState<Set<DspComplaintHistoryTab>>(new Set());

    // 默认按近 14 天 finished_at 过滤；用户在筛选栏选了范围则覆盖
    const finishedAtRange = useMemo(() => {
        if (dateRange) {
            const [start, end] = dateRange;
            const s = new Date(start);
            s.setHours(0, 0, 0, 0);
            const e = new Date(end);
            e.setHours(23, 59, 59, 999);
            return {from: s.toISOString(), to: e.toISOString()};
        }
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        const start = new Date();
        start.setDate(start.getDate() - (DEFAULT_LOOKBACK_DAYS - 1));
        start.setHours(0, 0, 0, 0);
        return {from: start.toISOString(), to: end.toISOString()};
    }, [dateRange]);

    const {data: unreadResp, refetch: refetchUnread} = useQuery<
        GetDspComplaintHistoryUnreadCountResponse,
        GetDspComplaintHistoryUnreadCountVariables
    >(GET_DSP_COMPLAINT_HISTORY_UNREAD_COUNT, {
        skip: !dspId || warehouseId == null,
        variables: {dspId: dspId ?? 0, warehouseId: warehouseId ?? 0},
        fetchPolicy: 'no-cache',
    });

    const [markTabAsRead] = useMutation<
        MarkDspComplaintHistoryTabAsReadResponse,
        MarkDspComplaintHistoryTabAsReadVariables
    >(MARK_DSP_COMPLAINT_HISTORY_TAB_AS_READ);

    const listFilter = useMemo(
        () => ({
            dspId: dspId ?? undefined,
            warehouseId: warehouseId ?? 0,
            // 已完结类：isFinished=1 走 idx_wh_dsp_finished（不传 statusList，前端按 status 本地分三子 Tab）
            isFinished: 1,
            complaintType: selectedType ?? undefined,
            finishedAtFrom: finishedAtRange.from,
            finishedAtTo: finishedAtRange.to,
            sortBy: 'FINISHED_AT' as const,
            sortDirection: 'DESC' as const,
        }),
        [dspId, warehouseId, selectedType, finishedAtRange.from, finishedAtRange.to],
    );

    const {data, loading, refetch} = useQuery<
        GetCustomerComplaintListByFiltersResponse,
        GetCustomerComplaintListByFiltersVariables
    >(GET_CUSTOMER_COMPLAINT_LIST_BY_FILTERS, {
        skip: !dspId || warehouseId == null,
        variables: {filter: listFilter},
        fetchPolicy: 'no-cache',
    });

    const items = useMemo(() => data?.getCustomerComplaintListByFilters ?? [], [data]);

    // 进入页面默认清掉 APPEAL_APPROVED；切 tab 时清对应 tab
    useEffect(() => {
        if (!dspId || warehouseId == null) return;
        const tab = activeTab;
        // 切 tab 时重拉已完结全量列表，保证三个 tab 展示的都是最新数据（非进页面时的旧快照）
        refetch({filter: listFilter}).catch(() => undefined);
        // 先把对应 tab 的红点本地标记为已读（即时反馈，遮住 refetch 返回前的旧未读数），再发起后端 mutation
        setLocallyReadTabs((prev) => {
            if (prev.has(tab)) return prev;
            const next = new Set(prev);
            next.add(tab);
            return next;
        });
        markTabAsRead({variables: {dspId, warehouseId, tab}})
            .then(() => refetchUnread())
            .then(() => {
                setLocallyReadTabs((prev) => {
                    if (!prev.has(tab)) return prev;
                    const next = new Set(prev);
                    next.delete(tab);
                    return next;
                });
            })
            .catch(() => undefined);
    }, [activeTab, dspId, warehouseId, markTabAsRead, refetchUnread, refetch, listFilter]);

    const handleRefresh = async () => {
        if (!dspId) return;
        await refetch({filter: listFilter});
        await refetchUnread();
    };

    // 三个 tab 各自的数据（按 keyword 过滤后按 status 分组；complaintType 已在后端筛选）
    const {appealApprovedItems, appealRejectedItems, acknowledgedItems} = useMemo(() => {
        const filtered = items.filter((it: CustomerComplaint) => {
            if (!matchKeyword(it, keyword)) return false;
            return true;
        });
        return {
            appealApprovedItems: filtered.filter((it) =>
                TAB_TO_STATUS_LIST.APPEAL_APPROVED.includes(it.status),
            ),
            appealRejectedItems: filtered.filter((it) =>
                TAB_TO_STATUS_LIST.APPEAL_REJECTED.includes(it.status),
            ),
            acknowledgedItems: filtered.filter((it) =>
                TAB_TO_STATUS_LIST.ACKNOWLEDGED.includes(it.status),
            ),
        };
    }, [items, keyword]);

    const filteredItems = useMemo(() => {
        if (activeTab === 'APPEAL_APPROVED') return appealApprovedItems;
        if (activeTab === 'APPEAL_REJECTED') return appealRejectedItems;
        return acknowledgedItems;
    }, [activeTab, appealApprovedItems, appealRejectedItems, acknowledgedItems]);

    const renderTabTitle = useCallback(
        (label: string, count: number, unread: number) => (
            <Badge content={unread > 0 ? Badge.dot : null}>
                <span className="px-1">
                    {label}({count})
                </span>
            </Badge>
        ),
        [],
    );

    const renderInfoRow = (item: CustomerComplaint) => (
        <div className="text-sm text-gray-500">
            {t('pages.dsp.home.customerComplaint.history.finishedAt', '完结时间')}:{' '}
            {formatTime(item.finishedAt)}
        </div>
    );

    const renderTag = (item: CustomerComplaint) => {
        if (activeTab !== 'ACKNOWLEDGED') return null;
        return (
            <Tag
                color={ACKED_TAB_STATUS_TAG_COLOR[item.status] ?? 'default'}
                style={{borderRadius: 4}}
            >
                {getStatusText(item.status)}
            </Tag>
        );
    };

    const unread = unreadResp?.getDspComplaintHistoryUnreadCount;

    return (
        <div className="bg-gray-50 min-h-screen">
            {/* 全局扫码层：ComplaintFilterBar 点扫码图标 setIsVisible(true) 后由此渲染相机 */}
            {isOnline && <BarCodeScanner showManualInputIcon={false} />}
            <div {...getNavBarContainerStyle()} className="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm">
                <NavBar onBack={() => navigate(-1)} backIcon={<ArrowLeftOutlined />}>
                    {t('pages.dsp.home.customerComplaint.history.title', '历史问题件')}
                </NavBar>
            </div>

            <div className="pt-12 bg-white">
                <ComplaintFilterBar
                    keyword={keyword}
                    onKeywordChange={setKeyword}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    selectedType={selectedType}
                    onSelectedTypeChange={setSelectedType}
                    complaintTypeOptions={DSP_VISIBLE_COMPLAINT_TYPES}
                    dateRangeLabel={t(
                        'pages.dsp.home.customerComplaint.history.dropdownDate',
                        '完结时间',
                    ) as string}
                />

                <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as DspComplaintHistoryTab)}>
                    <Tabs.Tab
                        title={renderTabTitle(
                            t('pages.dsp.home.customerComplaint.history.tabs.APPEAL_APPROVED', '申诉通过'),
                            appealApprovedItems.length,
                            locallyReadTabs.has('APPEAL_APPROVED') ? 0 : (unread?.appealApprovedUnread ?? 0),
                        )}
                        key="APPEAL_APPROVED"
                    />
                    <Tabs.Tab
                        title={renderTabTitle(
                            t('pages.dsp.home.customerComplaint.history.tabs.APPEAL_REJECTED', '申诉驳回'),
                            appealRejectedItems.length,
                            locallyReadTabs.has('APPEAL_REJECTED') ? 0 : (unread?.appealRejectedUnread ?? 0),
                        )}
                        key="APPEAL_REJECTED"
                    />
                    <Tabs.Tab
                        title={renderTabTitle(
                            t('pages.dsp.home.customerComplaint.history.tabs.ACKNOWLEDGED', '已完结'),
                            acknowledgedItems.length,
                            locallyReadTabs.has('ACKNOWLEDGED') ? 0 : (unread?.acknowledgedUnread ?? 0),
                        )}
                        key="ACKNOWLEDGED"
                    />
                </Tabs>
            </div>

            <PullToRefresh onRefresh={handleRefresh}>
                {loading && items.length === 0 ? (
                    <Skeleton.Paragraph lineCount={6} animated />
                ) : filteredItems.length === 0 ? (
                    <Empty description={t('pages.dsp.home.customerComplaint.empty', '暂无数据')} />
                ) : (
                    <div className="px-3 py-2 space-y-2">
                        {filteredItems.map((item: CustomerComplaint) => (
                            <ComplaintCard
                                key={item.id}
                                item={item}
                                tag={renderTag(item)}
                                infoRow={renderInfoRow(item)}
                                onClick={() =>
                                    navigate(
                                        DspPath.DELIVERY_DSP_COMPLAINT_DETAIL.replace(
                                            ':complaintId',
                                            String(item.id),
                                        ),
                                    )
                                }
                            />
                        ))}
                    </div>
                )}
            </PullToRefresh>
        </div>
    );
};

export default CustomerComplaintHistoryPage;
